import CustomButton from "@/components/common/Button";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import NotFound from "@/components/common/NotFound";
import { notify } from "@/components/common/Notification";
import StateMasterBadge from "@/components/StateMasterBadge";
import { checkFormChanges } from "@/lib/checkFormChanges";
import { WorkflowActionService } from "@/services/APIs";
import navigationRoutes from "@/utils/navigationRoutes";
import { ActionMaster } from "@/utils/types/actionMasters";
import { PageHeader } from "@ant-design/pro-components";
import { useQuery } from "@tanstack/react-query";
import { Card, ColorPicker, Form, Input } from "antd";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import useAdminNavigate from "@/hooks/useAdminNavigate";

const ActionMasterFormPage: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useAdminNavigate();

  const { id } = useParams();
  const isEditMode = !!id;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewActionMaster, setPreviewActionMaster] = useState<
    Partial<ActionMaster>
  >({});

  // Use React Query for data fetching
  const { data: actionMaster, isLoading } = useQuery({
    queryKey: ["action-master", id],
    queryFn: () => WorkflowActionService.getActionMasterById(id ?? ""),
    enabled: !!id,
  });

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitting(true);

    const transformedData = {
      ...values,
      background_color:
        typeof values.background_color === "object"
          ? values.background_color?.toHexString()
          : values.background_color,
      text_color:
        typeof values.text_color === "object"
          ? values.text_color?.toHexString()
          : values.text_color,
    };

    if (isEditMode) {
      // Check for changes before updating
      if (
        !checkFormChanges(
          transformedData,
          actionMaster as Record<string, any>,
          notify
        )
      ) {
        setIsSubmitting(false);
        return;
      }

      const response = await WorkflowActionService.updateActionMaster(
        id ?? "",
        transformedData
      );
      if (response) {
        navigate(navigationRoutes.ACTION_MASTERS);
      }
    } else {
      const response =
        await WorkflowActionService.createActionMaster(transformedData);
      if (response) {
        navigate(navigationRoutes.ACTION_MASTERS);
      }
    }
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (isEditMode && actionMaster) {
      form.setFieldsValue(actionMaster);
      setPreviewActionMaster(actionMaster);
    }
  }, [isEditMode, actionMaster, form]);

  if (isEditMode && isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }
  if (isEditMode && !actionMaster) {
    return <NotFound />;
  }

  return (
    <Card
      title={
        <PageHeader
          className="custom-page-header"
          title={isEditMode ? "Edit Action" : "Add Action"}
          subTitle={
            <StateMasterBadge stateMaster={previewActionMaster ?? {}} />
          }
          onBack={() => window.history.back()}
          extra={
            <CustomButton
              type="primary"
              icon={<Save size={16} />}
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={isSubmitting}
              title={isEditMode ? "Save Action Changes" : "Save New Action"}
            >
              {isSubmitting
                ? "Saving..."
                : isEditMode
                  ? "Save Changes"
                  : "Save"}
            </CustomButton>
          }
        />
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="max-w-xl"
        onValuesChange={(_, allValues) => {
          setPreviewActionMaster(allValues);
        }}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please enter action name" }]}
        >
          <Input placeholder="Enter action name" />
        </Form.Item>
        <Form.Item
          name="background_color"
          label="Background Color"
          rules={[{ required: true, message: "Please enter background color" }]}
        >
          <ColorPicker
            value={form.getFieldValue("background_color")}
            onChange={(color: any) => {
              form.setFieldsValue({ background_color: color.toHexString() });
            }}
            showText
            format="hex"
          />
        </Form.Item>
        <Form.Item
          name="text_color"
          label="Text Color"
          rules={[{ required: true, message: "Please enter text color" }]}
        >
          <ColorPicker
            value={form.getFieldValue("text_color")}
            onChange={(color: any) => {
              form.setFieldsValue({ text_color: color.toHexString() });
            }}
            showText
            format="hex"
          />
        </Form.Item>
      </Form>
    </Card>
  );
};

export default ActionMasterFormPage;
